import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import {
  Tabs,
  Tab,
  Card,
  Box,
  Paper,
  Chip,
  Typography,
  Stack,
  Button,
  Pagination,
  Autocomplete,
  ListItemButton,
  Popper,
  TextField
} from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

export const CustomBoxContents = styled(Box)(({ theme }) => ({
  overflowY: "auto",
  height: "calc(100vh - 75px)",
  zIndex: 1000,
}));

export const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    background: `linear-gradient(120deg, ${theme.palette.primary.light}, ${theme.palette.info.main})`,
  },
}));

export const CustomPagination = styled(Pagination)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: "10px",
  "& .MuiPagination-ul": {
    justifyContent: "center",
  },
  "& .MuiPaginationItem-root": {
    color: "black",
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  "& .MuiPaginationItem-root.Mui-selected:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));

export const CustomDataGrid = styled(DataGrid)(({ theme }) => ({
  position: "relative",
  borderRadius: "12px", // มุมโค้งของ border
  borderColor: theme.palette.divider, // สีของ border
  "& .MuiDataGrid-columnSeparator": {
    display: "none", // ซ่อนเส้นแบ่งคอลัมน์ทั้งหมด
  },
  "& .MuiDataGrid-columnHeaders": {
    fontWeight: "600",
  },
  "& .MuiDataGrid-overlay": {
    color: "grey",
  },
  "& .MuiDataGrid-cell:focus": {
    outline: "none",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "white",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
    {
      outline: "none",
      boxShadow: "none",
    },
}));

// สร้าง custom Tab ที่มีระยะห่างระหว่างแท็บ
export const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: "none", // ป้องกันการเปลี่ยนเป็นตัวพิมพ์ใหญ่
  textShadow: "none",
  "&.Mui-selected": {
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.primary.light,
  },
}));

// สร้าง custom Tabs
export const CustomTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTabs-flexContainer": {
    backgroundColor: "white",
    borderTop: "1px solid",
    borderBottom: "1px solid",
    padding: "0px 20px",
    borderColor: theme.palette.divider,
  },
  "& .MuiTabs-indicator": {
    display: "none", // ปิดการแสดง indicator
  },
}));

export const CustomTabsChild = styled(Tabs)(({ theme }) => ({
  "& .MuiTabs-flexContainer": {
    display: "flex",
    gap: "16px",
  },
  "& .MuiTabs-indicator": {
    display: "none", // ปิดการแสดง indicator
  },
}));

export const CustomTabChild = styled(Tab)(({ theme }, isSelected) => ({
  textTransform: "none", // ป้องกันการเปลี่ยนเป็นตัวพิมพ์ใหญ่
  padding: "0px",
  textShadow: "none",
  borderRadius: "12px 12px 0px 0px", // มุมโค้งของ border
  minWidth: "110px",
  "&.Mui-selected": {
    fontWeight: "bold", // น้ำหนักตัวอักษร
    textShadow: "1px 1px 3px rgba(0, 0, 0, 0.3)",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)", // เพิ่มเงาเมื่อเลือก
    backgroundColor: "white",
  },
}));

// Custom styled Card
export const CustomCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "isBorder" && prop !== "transform",
})(({ theme, isBorder, transform = true }) => ({
  position: "relative",
  border: isBorder
    ? `1px solid ${theme.palette.primary.main}`
    : `1px solid #e0e0e0`, // ใช้ isBorder เพื่อกำหนดเส้นขอบ
  borderRadius: "12px", // มุมโค้งของ Card
  transition: "transform 0.3s, box-shadow 0.3s, border 0.3s", // เพิ่มเอฟเฟกต์ทรานซิชัน
  boxShadow: isBorder ? "2px 3px 10px rgba(31, 163, 124, 0.3)" : "none",
  "&:hover": {
    cursor: "pointer",
    transform: transform && "scale(1.05)", // ขยายเล็กน้อยเมื่อ hover
    boxShadow: "2px 3px 10px rgba(31, 163, 124, 0.3)",
  },
  padding: theme.spacing(2), // ระยะห่างภายใน Card
}));

// Custom styled Paper
export const CustomPaper = styled(Paper)(({ theme }) => ({
  marginTop: "12px",
  borderRadius: "12px", // มุมโค้งของ Paper
  padding: theme.spacing(3), // ระยะห่างภายใน Paper
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", // เงาสวยงามแบบ soft shadow
  border: "1px solid transparent", // ขอบโปร่งใสปกติ
  "&:hover": {
    boxShadow: "0 6px 25px rgba(0, 0, 0, 0.2)", // เพิ่มเงาเมื่อ hover
  },
}));

export const CustomMyTab = styled(Tab)(({ theme }) => ({
  textTransform: "none", // ป้องกันการเปลี่ยนเป็นตัวพิมพ์ใหญ่
  fontSize: "12px", // ขนาดตัวอักษร
  fontWeight: "bold", // น้ำหนักตัวอักษร
  color: theme.palette.text.primary, // ใช้สีจากธีม
  borderRight: "1px solid", // เพิ่ม border
  borderColor: theme.palette.divider, // สีของ border
  "&.Mui-selected": {
    color: "black", // สีเมื่อเลือกแท็บ
  },
}));

export const CustomMyTabs = styled(Tabs)(({ theme }) => ({
  border: "1px solid", // เพิ่ม border
  borderRadius: "12px", // มุมโค้งของ border
  borderColor: theme.palette.divider, // สีของ border
  boxShadow: "0 4px 7px rgba(0, 0, 0, 0.1)", // เงาสวยงามแบบ soft shadow
  "& .MuiTabs-indicator": {
    display: "none", // ปิดการแสดง indicator
  },
}));

export const CustomChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "status",
})(({ theme, status, isSelected }) => {
  const backgroundImage = {
    All: `linear-gradient(to right bottom, ${theme.palette.grey[700]}, ${theme.palette.grey[900]})`,
    Online: `linear-gradient(to right bottom, ${theme.palette.info.main}, ${theme.palette.primary.main})`,
    Offline: `linear-gradient(to right bottom, ${theme.palette.grey.main}, ${theme.palette.grey.dark})`,
    Warning: `linear-gradient(to right bottom, ${theme.palette.warning.light}, ${theme.palette.warning.main})`,
    Error: `linear-gradient(to right bottom, ${theme.palette.error.light}, ${theme.palette.error.main})`,
    Sync: `linear-gradient(to right bottom, #ffe082, #ffb300)`,
  };
  return {
    color: isSelected ? "black" : "white",
    backgroundImage: isSelected ? backgroundImage[status] : `linear-gradient(to right bottom, #ffe082, #ffe082)`,
    boxShadow: isSelected ? "0 4px 6px rgba(0, 0, 0, 0.1)" : "none", // เพิ่มเงา
    border: isSelected ? `1px solid rgb(177, 111, 30)` : "none",
    borderRadius: "15px", // มุมโค้ง
    "&:hover": {
      cursor: "pointer",
    },
  };
});

export const BlackSpan = styled("span")({
  fontSize: "12px",
  fontWeight: "600",
  color: "grey",
  textShadow: "none",
});

export const GradientTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.5)",
}));

export const HeaderTypography = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  color: theme.palette.grey[800],
  padding: "15px",
  fontWeight: 600, // น้ำหนักตัวอักษร
}));

export const CustomStackGrid = styled(Stack)(({ theme }) => ({
  display: "flex",
  justifyContent: "start",
  alignItems: "center",
  width: "100%",
  height: "100%",
  cursor: "pointer",
}));

export const GradientTypographyGrid = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "start",
  alignItems: "center",
  width: "100%",
  height: "100%",
  fontSize: "15px",
}));

export const CustomStackRow = styled(Stack)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%",
}));

export const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

export const AccordionSummary = styled((props) => (
  <MuiAccordionSummary {...props} />
))(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(255, 255, 255, .05)",
  }),
}));

export const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(1),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export const CustomButton = styled(Button)(({ theme, status }) => {
  const backgroundImage = {
    warning: `linear-gradient(to right bottom, ${theme.palette.warning.light}, ${theme.palette.warning.main})`,
    error: `linear-gradient(to right bottom, ${theme.palette.error.light}, ${theme.palette.error.main})`,
  };
  return {
    width: "80px",
    height: "38px",
    backgroundImage: backgroundImage[status],
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", // เพิ่มเงา
    "&:hover": {
      boxShadow: "0px 4px 5px rgba(0, 0, 0, 0.2)",
    },
  };
});

export const StyledListItemButton = styled(ListItemButton)(
  ({ selected }) => ({
    background: selected ? 'linear-gradient(to right bottom, #fff176, #f57f17)' : 'transparent', // gradient ตอน hover
    border: selected ? '1px solid rgb(151, 101, 0)' : '1px solid transparent',
    borderRadius: '15px',
    padding: '0px 10px',
    transition: 'background 0.3s ease',
    '&:hover': {
      border: '1px solid rgb(151, 101, 0)',
    },
  })
);

export const CustomAutocompleteBill = styled(Autocomplete)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid rgb(221, 221, 221)",
      borderRadius: "0px",
      cursor: "pointer",
    },
    height: "37px",
    fontWeight: "600",
    fontSize: "18px",
  },
}));

export const CustomAutocomplete = styled(Autocomplete)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      maxHeight: "32px",
      cursor: "pointer",
    },
    height: "32px",
    fontSize: "0.8rem",
  },
  "& .MuiInputBase-input": {
    padding: "4px 6px",
    fontSize: "0.8rem",
    cursor: "pointer",
  },
  "& .MuiAutocomplete-tag": {
    height: "20px",
    maxWidth: "150px",
    fontSize: "0.75rem",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  },
  "& .MuiChip-deleteIcon": {
    display: "none",
  },
}));

export const StyledPopper = styled(Popper)(({ theme }) => ({
  '& .MuiAutocomplete-paper': {
    fontSize: "0.8rem",
    borderRadius: '12px',
    boxShadow: 'none',
    border: '2px solid #e0e0e0',
    "& .MuiAutocomplete-popper": {
      backgroundColor: "none",
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
      backgroundColor: 'transparent !important',
    },
  },
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  // ซ่อน endAdornment โดย default
  '& .MuiAutocomplete-endAdornment': {
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  // แสดงตอนโฟกัส
  '&:focus-within .MuiAutocomplete-endAdornment': {
    opacity: 1,
    marginTop: "10px",
  },
  '& .MuiInputBase-root': {
    alignItems: 'center', // ✅ จัดแนว tag ให้อยู่กึ่งกลางแนวตั้ง
    paddingTop: '4px',
    paddingBottom: '4px',
  },
}));

export const CustomBoxLiff = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  height: '100%',
  backgroundColor: "white",
  zIndex: 50,
  width: "100%",
  fontSize: "20px",
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  paddingLeft: "1rem",
}));

export const CustomBoxContentsLiff = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  height: '100vh',
  backgroundColor: "white",
  zIndex: 50,
  width: "100%",
}));